import ShareIcon from '@mui/icons-material/Share';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useRef, useState } from 'react';
import { useFlySafeTranslation } from '../utils/i18n';
import { buildShareUrl } from '../utils/shareParams';
import type { ShareParams } from '../utils/shareParams';
import type { Mode } from '../utils/types';

interface ShareButtonProps {
  mode: Mode;
  timeSpan: 'day' | 'week';
  showVPTS: boolean;
  showVID: boolean;
  animationWindow: [Date, Date] | null | undefined;
  stationIds: string[];
}

const ShareButton: React.FC<ShareButtonProps> = ({
  mode,
  timeSpan,
  showVPTS,
  showVID,
  animationWindow,
  stationIds,
}) => {
  const { t } = useFlySafeTranslation();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleClick = useCallback(async () => {
    const params: ShareParams = {
      mode,
      timeSpan,
      showVPTS,
      showVID,
    };

    if (animationWindow) {
      params.animationStart = animationWindow[0].toISOString();
      params.animationEnd = animationWindow[1].toISOString();
    }

    if (stationIds.length > 0) {
      params.stationIds = stationIds;
    }

    const url = buildShareUrl(params);

    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Silently fail in insecure contexts
    }

    setCopied(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  }, [mode, timeSpan, showVPTS, showVID, animationWindow, stationIds]);

  return (
    <Tooltip
      open={copied || undefined}
      title={copied ? t('flysafe-share-copied') : t('flysafe-share-tooltip')}
    >
      <IconButton
        aria-label={t('flysafe-share-tooltip')}
        onClick={handleClick}
        size="small"
      >
        <ShareIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

export default ShareButton;
